import type { ApplicationEnum } from "~/@types/enums";
import type { ReportDraftKind } from "~reports/helpers/buildReportDraftInput";

import { GraphQLClient, gql } from "graphql-request";

import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import answerRating from "~/helpers/aiChat/answerRating";
import chatTurn from "~/helpers/aiChat/buildFilePrompt";
import drainSseEvents, { type RawSseEvent } from "~/helpers/aiChat/parseSseStream";
import logger from "~/helpers/logger";
import unauthorizedRetryPolicy, {
	MutationNotReplayedAfterRefreshError,
	type UnauthorizedRetryContext
} from "~/helpers/unauthorizedRetryPolicy";

export type ChatModule = ApplicationEnum | "ONBOARDING";

export type ToolCallStatus = "ERROR" | "SUCCESS";

export type ChatRole = "ASSISTANT" | "USER";

export type AnswerRating = "DOWN" | "UP";
export type RatingReasonCategory = "COULD_NOT_DO" | "DID_NOT_ANSWER" | "OTHER" | "UNHELPFUL_REDIRECT" | "WRONG";
export interface RatingDetails {
	category: RatingReasonCategory;
	followUpRequested: boolean;
	reason: string;
}

export type ProposalState = "CANCELLED" | "COMMITTING" | "CONFIRMED" | "PENDING";

export type FernyActionKind = "APPLY_GRID_FILTER" | "NAVIGATE_TO" | "OPEN_PANEL" | "START_WALKTHROUGH";

export interface FernyActionResult {
	deepLink: string;
	kind: FernyActionKind;
	reachedDestination?: string;
	requestedDestination: string;
	walkthroughId?: string;
}

export interface AvailableWalkthrough {
	id: string;
	title: string;
}

export interface ScreenContext {
	appVersion?: string;
	availableWalkthroughs?: AvailableWalkthrough[];
	route: string;
	screenLabel?: string;
}

export type IntakeKind = "BUG" | "FEATURE_REQUEST";

export type IntakeSeverity = "BLOCKING" | "CRITICAL" | "HIGH" | "LOW" | "MEDIUM";

export interface IntakeContextResult {
	appVersion: string;
	pageLabel: string;
	tenantName: string;
	userName: string;
}

export interface IntakeCaptureResult {
	captureId: string;
	context: IntakeContextResult;
	description: string;
	intakeKind: IntakeKind;
	severity?: IntakeSeverity;
	title: string;
}

export interface IntakeConfirmationResult {
	kind: IntakeKind;
	referenceId?: string;
	status: string;
	title: string;
}

export interface ToolCallResult {
	result?: string;
	status: ToolCallStatus;
	toolName: string;
}

export type PriceImportApplyOutcome =
	| "APPROVAL_NOT_ACTIVE"
	| "COMPLETE"
	| "INTERRUPTED"
	| "MORE_TO_APPLY"
	| "REFUSED"
	| "STOPPED";

export interface PriceImportPriceToday {
	asOf?: null | string;
	basePerEach: string;
	effectiveDate?: null | string;
	knownCostPerEach?: null | string;
	royaltyIncluded: boolean;
	royaltyPerEach?: null | string;
	royaltyState: string;
}

export interface PriceImportCardRow {
	basePerEach?: null | string;
	comparison: string;
	description: string;
	disposition: string;
	effectiveDate?: null | string;
	itemCode: string;
	knownCostPerEach?: null | string;
	note: string;
	royaltyIncluded: boolean;
	royaltyPerEach?: null | string;
	royaltyState: string;
	sourceRowId: string;
	today?: null | PriceImportPriceToday;
}

export interface PriceImportCard {
	expiresAt: string;
	fileName: string;
	reviewLink: string;
	reviewRevision: number;
	rows: PriceImportCardRow[];
	selectedRowCount: number;
	summaryLines: string[];
	todayNote: string;
	vendorName: string;
}

export interface PriceImportApplyRow {
	createdMaterial: boolean;
	knownSubtotalPerEach?: null | string;
	label: string;
	materialItemId?: null | string;
	priceId?: null | string;
	royaltyStatus?: null | string;
	sourceRowId: string;
	status: string;
}

export interface PriceImportApply {
	message: string;
	outcome: PriceImportApplyOutcome;
	resultsLink: string;
	rows: PriceImportApplyRow[];
	rowsNotSavedYet: number;
	rowsWaiting: number;
}

export interface MutationProposalResult {
	argumentsJson: string;
	id: string;
	priceImport?: null | PriceImportCard;
	state: ProposalState;
	summary: string;
	toolName: string;
}

export interface AssistantTurn {
	actions: FernyActionResult[];
	artifacts?: ArtifactResult[];
	assistantMessage: string;
	intakeCaptures: IntakeCaptureResult[];
	layouts?: AnswerLayoutResult[];
	proposals: MutationProposalResult[];
	toolCalls: ToolCallResult[];
	usage: TokenUsage;
}

export type ChatContinuation = AssistantTurn;

export interface MutationResolutionResult {
	continuation: ChatContinuation | null;
	id: string;
	intake?: IntakeConfirmationResult | null;
	priceImportApply?: null | PriceImportApply;
	state: ProposalState;
	summary: string;
	toolName: string;
}

export interface TokenUsage {
	inputTokens: number;
	outputTokens: number;
}

export interface ArtifactResult {
	contentBase64: string;
	filename: string;
	mimeType: string;
}

export interface AnswerTileResult {
	label: string;
	scope: string;
	value: string;
}

export interface AnswerColumnResult {
	label: string;
	type: string;
}

export interface AnswerLayoutSectionResult {
	columns: AnswerColumnResult[];
	heading: string;
	partial: boolean;
	rows: string[][];
	tiles: AnswerTileResult[];
}

export interface AnswerLayoutResult {
	sections: AnswerLayoutSectionResult[];
	title: string;
	version: number;
}

export interface ChatResult extends AssistantTurn {
	artifacts: ArtifactResult[];
	id: string;
}

export interface ChatMessageResult {
	content: string;
	id: string;
	rating: AnswerRating | null;
	role: ChatRole;
	sessionId: string;
	timestamp: string;
	toolCalls: ToolCallResult[];
}

export interface ChatSessionSummary {
	createDate: string;
	id: string;
	lastActivity: string;
	title: string | null;
	totalInputTokens: number;
	totalOutputTokens: number;
	userEmail: string;
}

export type SuggestionTier = "CHECK" | "NONE" | "SUGGESTED";

export interface CsvColumnSample {
	header: string;
	values: string[];
}

export interface SuggestCsvColumnMappingInput {
	columnSamples: CsvColumnSample[];
	fileHeaders: string[];
	requiredFields: string[];
	templateFields: string[];
	templateFileName: string;
}

export interface CsvColumnMappingSuggestion {
	confidence: number;
	fileHeader: string | null;
	templateField: string;
	tier: SuggestionTier;
}

export interface CsvColumnMappingSuggestionResult {
	fields: CsvColumnMappingSuggestion[];
	suggestionId: string | null;
}

export interface AcceptedCsvColumnMapping {
	fileHeader: string | null;
	templateField: string;
}

export type RecommendationSource = "EXACT_MATCH" | "JEV";

export interface MissingDataRecommendation {
	confidence: number;
	findingKey: string;
	proposedId: string;
	source: RecommendationSource;
	suggestionId: string;
	tier: SuggestionTier;
}

export type MissingDataFixOutcome = "ACCEPTED" | "CHANGED" | "DISMISSED";

export interface PageCandidate {
	id: string;
	label: string;
}

export interface PageSearchMatch {
	confidence: number;
	pageId: string;
	tier: SuggestionTier;
}

export interface ItemSearchMatch {
	confidence: number;
	itemId: string;
	name: string;
	sku: string;
	tier: SuggestionTier;
}

export interface ReportDraftLabelSize {
	height: number;
	unit: "in" | "mm";
	width: number;
}

export interface ReportDraftFileInput {
	contentBase64?: string;
	mediaType: string;
	name: string;
	text?: string;
}

export interface DesignReportDraftInput {
	dataSourceId: string;
	fieldsJson: string;
	file: ReportDraftFileInput;
	kind: ReportDraftKind;
	labelSize?: ReportDraftLabelSize;
}

export interface CheckReportDraftInput {
	fieldsJson: string;
	file: ReportDraftFileInput;
	outlineJson: string;
	previewPdfBase64: string;
	runId: string;
}

export interface ReportDraftLocation {
	height: number;
	page: number;
	width: number;
	x: number;
	y: number;
}

export interface ReportDraftMatch {
	fieldPath: string;
	label: string;
}

export interface ReportDraftGap {
	label: string;
	location: ReportDraftLocation | null;
	note: string | null;
	suggestedFieldPath: string | null;
}

export interface ReportDraftError {
	code: string;
	message: string;
}

export interface ReportDraftPayload {
	errors: ReportDraftError[] | null;
	gaps: ReportDraftGap[] | null;
	matches: ReportDraftMatch[] | null;
	outlineJson: string | null;
	runId: string | null;
}

export interface ReportDraftAvailability {
	enabled: boolean;
	maxFileBytes: number;
	maxPages: number;
	remainingThisMonth: number;
}

export interface CustomerSearchMatch {
	confidence: number;
	customerId: string;
	erpId: string;
	lookupCode: string;
	name: string;
	tier: SuggestionTier;
}

export type SuggestionPurpose = "CLASSIFY" | "FILL" | "FIND" | "SAME_AS";

export interface SuggestionCandidate {
	detail?: string;
	id: string;
	label: string;
}

export interface SuggestFromListQuestion {
	candidates: SuggestionCandidate[];
	limit: number;
	listName: string;
	purpose: SuggestionPurpose;
	wording: string;
}

export interface SuggestionMatch {
	candidateId: string;
	tier: SuggestionTier;
}

export interface SuggestFromListResult {
	matches: SuggestionMatch[];
	suggestionId: string | null;
}

export type SuggestionOutcome = "CHANGED" | "IGNORED" | "USED";

export interface RecordSuggestionOutcomeInput {
	candidateId?: string;
	outcome: SuggestionOutcome;
	suggestionId: string;
}

interface ChatPayloadResponse {
	sendChatMessage: {
		errors: { message: string }[] | null;
		result: ChatResult | null;
	};
}

interface ConfirmMutationPayloadResponse {
	confirmMutation: {
		errors: { message: string }[] | null;
		result: MutationResolutionResult | null;
	};
}

interface CancelMutationPayloadResponse {
	cancelMutation: {
		errors: { message: string }[] | null;
		result: MutationResolutionResult | null;
	};
}

interface EndSessionPayloadResponse {
	endChatSession: {
		errors: { message: string }[] | null;
		session: { sessionId: string } | null;
	};
}

interface OpenSessionPayloadResponse {
	openChatSession: {
		errors: { message: string }[] | null;
		session: { id: string } | null;
	};
}

interface SessionHistoryPayloadResponse {
	chatSessionHistory: {
		errors: { message: string }[] | null;
		messages: ChatMessageResult[] | null;
	};
}

interface SessionAnswersPayloadResponse {
	chatSessionHistory: {
		errors: { message: string }[] | null;
		messages: Pick<ChatMessageResult, "id" | "rating" | "role">[] | null;
	};
}

interface RateMessagePayloadResponse {
	rateChatMessage: {
		errors: { message: string }[] | null;
		message: { messageId: string; rating: AnswerRating | null } | null;
	};
}

interface SessionListPayloadResponse {
	chatSessions: {
		errors: { message: string }[] | null;
		sessions: ChatSessionSummary[] | null;
	};
}

interface DeleteSessionPayloadResponse {
	deleteChatSession: {
		errors: { message: string }[] | null;
		session: { sessionId: string } | null;
	};
}

interface SuggestCsvColumnMappingPayloadResponse {
	suggestCsvColumnMapping: CsvColumnMappingSuggestionResult & {
		errors: { message: string }[] | null;
	};
}

interface RecordCsvColumnMappingOutcomePayloadResponse {
	recordCsvColumnMappingOutcome: {
		decisionId: string | null;
		errors: { message: string }[] | null;
	};
}

interface RecommendMissingDataFixPayloadResponse {
	recommendMissingDataFix: {
		errors: { message: string }[] | null;
		recommendation: MissingDataRecommendation | null;
	};
}

interface RecordMissingDataFixOutcomePayloadResponse {
	recordMissingDataFixOutcome: {
		decisionId: string | null;
		errors: { message: string }[] | null;
	};
}

interface SuggestPageMatchesPayloadResponse {
	suggestPageMatches: {
		errors: { message: string }[] | null;
		pages: PageSearchMatch[];
	};
}

interface SuggestItemSearchMatchesPayloadResponse {
	suggestItemSearchMatches: {
		errors: { message: string }[] | null;
		items: ItemSearchMatch[];
	};
}

interface ReportDraftAvailabilityResponse {
	reportDraftAvailability: ReportDraftAvailability;
}

interface DesignReportDraftResponse {
	designReportDraft: ReportDraftPayload;
}

interface CheckReportDraftResponse {
	checkReportDraft: ReportDraftPayload;
}

interface SuggestCustomerSearchMatchesPayloadResponse {
	suggestCustomerSearchMatches: {
		customers: CustomerSearchMatch[];
		errors: { message: string }[] | null;
	};
}

interface SuggestFromListPayloadResponse {
	suggestFromList: SuggestFromListResult & {
		errors: { message: string }[] | null;
	};
}

interface SuggestFromListBatchPayloadResponse {
	suggestFromListBatch: {
		errors: { message: string }[] | null;
		results: (SuggestFromListResult & { errors: { message: string }[] | null })[];
	};
}

interface RecordSuggestionOutcomePayloadResponse {
	recordSuggestionOutcome: {
		errors: { message: string }[] | null;
		recorded: boolean;
	};
}

const INTAKE_CAPTURES_SELECTION = `
	intakeCaptures {
		captureId
		intakeKind
		title
		description
		severity
		context {
			pageLabel
			appVersion
			tenantName
			userName
		}
	}
`;

const PRICE_IMPORT_SELECTION = `
	priceImport {
		vendorName
		fileName
		reviewRevision
		selectedRowCount
		summaryLines
		expiresAt
		reviewLink
		todayNote
		rows {
			sourceRowId
			description
			itemCode
			disposition
			effectiveDate
			basePerEach
			royaltyPerEach
			royaltyState
			knownCostPerEach
			royaltyIncluded
			note
			comparison
			today {
				asOf
				effectiveDate
				basePerEach
				royaltyPerEach
				royaltyState
				knownCostPerEach
				royaltyIncluded
			}
		}
	}
`;

const PRICE_IMPORT_APPLY_SELECTION = `
	priceImportApply {
		outcome
		rowsNotSavedYet
		rowsWaiting
		message
		resultsLink
		rows {
			sourceRowId
			status
			label
			materialItemId
			priceId
			createdMaterial
			royaltyStatus
			knownSubtotalPerEach
		}
	}
`;

const SEND_CHAT_MESSAGE = gql`
	mutation SendChatMessage($input: SendChatMessageInput!) {
		sendChatMessage(input: $input) {
			result {
				id
				assistantMessage
				artifacts {
					filename
					mimeType
					contentBase64
				}
				layouts {
					version
					title
					sections {
						heading
						partial
						tiles {
							label
							value
							scope
						}
						columns {
							label
							type
						}
						rows
					}
				}
				toolCalls {
					toolName
					status
					result
				}
				proposals {
					id
					toolName
					argumentsJson
					summary
					state
					${PRICE_IMPORT_SELECTION}
				}
				actions {
					kind
					requestedDestination
					deepLink
					reachedDestination
					walkthroughId
				}
				${INTAKE_CAPTURES_SELECTION}
				usage {
					inputTokens
					outputTokens
				}
			}
			errors {
				message
			}
		}
	}
`;

const OPEN_CHAT_SESSION = gql`
	mutation OpenChatSession($input: OpenSessionInput!) {
		openChatSession(input: $input) {
			session {
				id
			}
			errors {
				message
			}
		}
	}
`;

const CONFIRM_MUTATION = gql`
	mutation ConfirmMutation($input: ConfirmMutationInput!) {
		confirmMutation(input: $input) {
			result {
				id
				toolName
				state
				summary
				${PRICE_IMPORT_APPLY_SELECTION}
				intake {
					kind
					status
					referenceId
					title
				}
				continuation {
					assistantMessage
					toolCalls {
						toolName
						status
						result
					}
					proposals {
						id
						toolName
						argumentsJson
						summary
						state
						${PRICE_IMPORT_SELECTION}
					}
					actions {
						kind
						requestedDestination
						deepLink
						reachedDestination
						walkthroughId
					}
					${INTAKE_CAPTURES_SELECTION}
					usage {
						inputTokens
						outputTokens
					}
				}
			}
			errors {
				message
			}
		}
	}
`;

const CANCEL_MUTATION = gql`
	mutation CancelMutation($input: CancelMutationInput!) {
		cancelMutation(input: $input) {
			result {
				id
				toolName
				state
				summary
			}
			errors {
				message
			}
		}
	}
`;

const END_CHAT_SESSION = gql`
	mutation EndChatSession($input: EndSessionInput!) {
		endChatSession(input: $input) {
			session {
				sessionId
			}
			errors {
				message
			}
		}
	}
`;

const GET_CHAT_SESSION_HISTORY = gql`
	query GetChatSessionHistory($input: GetSessionInput!) {
		chatSessionHistory(input: $input) {
			messages {
				id
				sessionId
				role
				content
				timestamp
				rating
				toolCalls {
					toolName
					status
					result
				}
			}
			errors {
				message
			}
		}
	}
`;

const GET_CHAT_SESSION_ANSWERS = gql`
	query GetChatSessionAnswers($input: GetSessionInput!) {
		chatSessionHistory(input: $input) {
			messages {
				id
				role
				rating
			}
			errors {
				message
			}
		}
	}
`;

const RATE_CHAT_MESSAGE = gql`
	mutation RateChatMessage($input: RateChatMessageInput!) {
		rateChatMessage(input: $input) {
			message {
				messageId
				rating
			}
			errors {
				message
			}
		}
	}
`;

const CHAT_SESSIONS = gql`
	query ChatSessions {
		chatSessions {
			sessions {
				id
				title
				createDate
				lastActivity
				totalInputTokens
				totalOutputTokens
				userEmail
			}
			errors {
				message
			}
		}
	}
`;

const DELETE_CHAT_SESSION = gql`
	mutation DeleteChatSession($input: DeleteSessionInput!) {
		deleteChatSession(input: $input) {
			session {
				sessionId
			}
			errors {
				message
			}
		}
	}
`;

const SUGGEST_CSV_COLUMN_MAPPING = gql`
	query SuggestCsvColumnMapping($input: SuggestCsvColumnMappingInput!) {
		suggestCsvColumnMapping(input: $input) {
			suggestionId
			fields {
				templateField
				fileHeader
				confidence
				tier
			}
			errors {
				message
			}
		}
	}
`;

const RECORD_CSV_COLUMN_MAPPING_OUTCOME = gql`
	mutation RecordCsvColumnMappingOutcome($input: RecordCsvColumnMappingOutcomeInput!) {
		recordCsvColumnMappingOutcome(input: $input) {
			decisionId
			errors {
				message
			}
		}
	}
`;

const RECOMMEND_MISSING_DATA_FIX = gql`
	query RecommendMissingDataFix($findingKey: String!) {
		recommendMissingDataFix(findingKey: $findingKey) {
			recommendation {
				suggestionId
				findingKey
				proposedId
				source
				confidence
				tier
			}
			errors {
				message
			}
		}
	}
`;

const RECORD_MISSING_DATA_FIX_OUTCOME = gql`
	mutation RecordMissingDataFixOutcome($input: RecordMissingDataFixOutcomeInput!) {
		recordMissingDataFixOutcome(input: $input) {
			decisionId
			errors {
				message
			}
		}
	}
`;

const SUGGEST_PAGE_MATCHES = gql`
	query SuggestPageMatches($searchText: String!, $pages: [PageCandidateInput!]!) {
		suggestPageMatches(searchText: $searchText, pages: $pages) {
			pages {
				pageId
				confidence
				tier
			}
			errors {
				message
			}
		}
	}
`;

const SUGGEST_ITEM_SEARCH_MATCHES = gql`
	query SuggestItemSearchMatches($searchText: String!) {
		suggestItemSearchMatches(searchText: $searchText) {
			items {
				itemId
				sku
				name
				confidence
				tier
			}
			errors {
				message
			}
		}
	}
`;

const REPORT_DRAFT_PAYLOAD_SELECTION = `
	runId
	outlineJson
	matches {
		label
		fieldPath
	}
	gaps {
		label
		suggestedFieldPath
		note
		location {
			page
			x
			y
			width
			height
		}
	}
	errors {
		code
		message
	}
`;

const REPORT_DRAFT_AVAILABILITY = gql`
	query ReportDraftAvailability {
		reportDraftAvailability {
			enabled
			remainingThisMonth
			maxFileBytes
			maxPages
		}
	}
`;

const DESIGN_REPORT_DRAFT = gql`
	mutation DesignReportDraft($input: DesignReportDraftInput!) {
		designReportDraft(input: $input) {
			${REPORT_DRAFT_PAYLOAD_SELECTION}
		}
	}
`;

const CHECK_REPORT_DRAFT = gql`
	mutation CheckReportDraft($input: CheckReportDraftInput!) {
		checkReportDraft(input: $input) {
			${REPORT_DRAFT_PAYLOAD_SELECTION}
		}
	}
`;

const SUGGEST_CUSTOMER_SEARCH_MATCHES = gql`
	query SuggestCustomerSearchMatches($searchText: String!) {
		suggestCustomerSearchMatches(searchText: $searchText) {
			customers {
				customerId
				name
				lookupCode
				erpId
				confidence
				tier
			}
			errors {
				message
			}
		}
	}
`;

const SUGGEST_FROM_LIST = gql`
	query SuggestFromList($input: SuggestFromListInput!) {
		suggestFromList(input: $input) {
			suggestionId
			matches {
				candidateId
				tier
			}
			errors {
				message
			}
		}
	}
`;

const SUGGEST_FROM_LIST_BATCH = gql`
	query SuggestFromListBatch($input: SuggestFromListBatchInput!) {
		suggestFromListBatch(input: $input) {
			results {
				suggestionId
				matches {
					candidateId
					tier
				}
				errors {
					message
				}
			}
			errors {
				message
			}
		}
	}
`;

const RECORD_SUGGESTION_OUTCOME = gql`
	mutation RecordSuggestionOutcome($input: RecordSuggestionOutcomeInput!) {
		recordSuggestionOutcome(input: $input) {
			recorded
			errors {
				message
			}
		}
	}
`;

async function getAccessToken(forceRefresh?: boolean): Promise<string> {
	return await sfAuth.checkSession(forceRefresh);
}

function buildEndpoint(): string {
	return Config.aiOrchestratorUrl || "http://localhost:5291/graphql";
}

export function buildOrchestratorHeaders(token: string, activeModule?: ChatModule): Record<string, string> {
	const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
	if (activeModule) headers["x-active-module"] = activeModule;
	return headers;
}

function createClient(token: string, activeModule?: ChatModule): GraphQLClient {
	return new GraphQLClient(buildEndpoint(), {
		headers: buildOrchestratorHeaders(token, activeModule)
	});
}

function extractResultOrThrow<T>(payload: { errors: { message: string }[] | null }, result: T | null): T {
	if (payload.errors && payload.errors.length > 0) throw new Error(payload.errors.map((e) => e.message).join("; "));
	if (!result) throw new Error("No result returned from AI orchestrator");
	return result;
}

interface GraphQLErrorResponse {
	errors?: { extensions?: { code?: string }; message?: string }[];
	status?: number;
}

interface ErrorWithResponse {
	response: GraphQLErrorResponse;
}

function hasResponse(error: unknown): error is ErrorWithResponse {
	return (
		!!error &&
		typeof error === "object" &&
		"response" in error &&
		!!error.response &&
		typeof error.response === "object"
	);
}

function hasMessage(error: unknown): error is { message: string } {
	return !!error && typeof error === "object" && "message" in error && typeof error.message === "string";
}

function isStatusUnauthorized(response: GraphQLErrorResponse): boolean {
	return response.status === 401;
}

function hasUnauthorizedGraphQLError(response: GraphQLErrorResponse): boolean {
	return (
		!!response.errors &&
		response.errors.some(
			(err) =>
				err.message?.includes("not authorized") ||
				err.message?.includes("unauthorized") ||
				err.extensions?.code === "UNAUTHENTICATED"
		)
	);
}

function isUnauthorizedMessage(message: string): boolean {
	return (
		message.includes("not authorized") ||
		message.includes("unauthorized") ||
		message.includes("The current user is not authorized")
	);
}

function isUnauthorizedError(error: unknown): boolean {
	if (hasResponse(error)) return isStatusUnauthorized(error.response) || hasUnauthorizedGraphQLError(error.response);
	if (hasMessage(error)) return isUnauthorizedMessage(error.message);
	return false;
}

type OrchestratorOperationType = UnauthorizedRetryContext["operationType"];

async function executeWithAuthRetry<T>(
	operationType: OrchestratorOperationType,
	request: (token: string) => Promise<T>
): Promise<T> {
	const token = await getAccessToken();
	try {
		return await request(token);
	} catch (error) {
		return await retryOnUnauthorized(operationType, error, request);
	}
}

async function retryOnUnauthorized<T>(
	operationType: OrchestratorOperationType,
	error: unknown,
	request: (token: string) => Promise<T>
): Promise<T> {
	if (!isUnauthorizedError(error)) throw error;
	const refreshedToken = await getAccessToken(true);
	const resolution = await unauthorizedRetryPolicy.resolveUnauthorized<T>(
		{ operationType, tokenRefreshed: !!refreshedToken },
		{
			redirectToLogin: () => sfAuth.redirectToLogin(),
			replay: () => request(refreshedToken)
		}
	);

	if (resolution.action === "redirectToLogin") throw error;
	if (resolution.action === "surfaceNotReplayed") throw new MutationNotReplayedAfterRefreshError();
	return resolution.replayed;
}

export interface ChatTurnInput {
	attachedContext: string | null;
	clientMessageId: string;
	message: string;
}

export async function sendChatMessage(
	turnInput: ChatTurnInput,
	sessionId?: string,
	activeModule?: ChatModule,
	screenContext?: ScreenContext
): Promise<ChatResult> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token, activeModule).request<ChatPayloadResponse>(SEND_CHAT_MESSAGE, {
			input: chatTurn.toRequestInput(turnInput, sessionId, screenContext)
		});
		const result = extractResultOrThrow(response.sendChatMessage, response.sendChatMessage.result);
		return {
			...result,
			actions: result.actions ?? [],
			artifacts: result.artifacts ?? [],
			intakeCaptures: result.intakeCaptures ?? []
		};
	});
}

function normalizeContinuation(continuation: ChatContinuation | null | undefined): ChatContinuation | null {
	if (!continuation) return null;
	return {
		...continuation,
		actions: continuation.actions ?? [],
		intakeCaptures: continuation.intakeCaptures ?? [],
		proposals: continuation.proposals ?? [],
		toolCalls: continuation.toolCalls ?? []
	};
}

export async function confirmMutation(proposalId: string): Promise<MutationResolutionResult> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<ConfirmMutationPayloadResponse>(CONFIRM_MUTATION, {
			input: { proposalId }
		});
		const result = extractResultOrThrow(response.confirmMutation, response.confirmMutation.result);
		return { ...result, continuation: normalizeContinuation(result.continuation) };
	});
}

export async function cancelMutation(proposalId: string): Promise<MutationResolutionResult> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<CancelMutationPayloadResponse>(CANCEL_MUTATION, {
			input: { proposalId }
		});
		return extractResultOrThrow(response.cancelMutation, response.cancelMutation.result);
	});
}

export async function endChatSession(sessionId: string): Promise<{ sessionId: string }> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<EndSessionPayloadResponse>(END_CHAT_SESSION, {
			input: { sessionId }
		});
		return extractResultOrThrow(response.endChatSession, response.endChatSession.session);
	});
}

export async function getChatSessionHistory(sessionId: string): Promise<ChatMessageResult[]> {
	return executeWithAuthRetry("query", async (token) => {
		const response = await createClient(token).request<SessionHistoryPayloadResponse>(GET_CHAT_SESSION_HISTORY, {
			input: { sessionId }
		});
		return extractResultOrThrow(response.chatSessionHistory, response.chatSessionHistory.messages);
	});
}

export async function getLatestAnswer(
	sessionId: string
): Promise<Pick<ChatMessageResult, "id" | "rating"> | undefined> {
	return executeWithAuthRetry("query", async (token) => {
		const response = await createClient(token).request<SessionAnswersPayloadResponse>(GET_CHAT_SESSION_ANSWERS, {
			input: { sessionId }
		});
		const messages = extractResultOrThrow(response.chatSessionHistory, response.chatSessionHistory.messages);
		return messages.filter((message) => message.role === "ASSISTANT").at(-1);
	});
}

export async function rateChatMessage(
	sessionId: string,
	messageId: string,
	rating: AnswerRating | null,
	details?: RatingDetails
): Promise<AnswerRating | null> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<RateMessagePayloadResponse>(RATE_CHAT_MESSAGE, {
			input: answerRating.buildRateInput(sessionId, messageId, rating, details)
		});
		return extractResultOrThrow(response.rateChatMessage, response.rateChatMessage.message).rating;
	});
}

export async function fetchChatSessions(): Promise<ChatSessionSummary[]> {
	return executeWithAuthRetry("query", async (token) => {
		const response = await createClient(token).request<SessionListPayloadResponse>(CHAT_SESSIONS);
		return extractResultOrThrow(response.chatSessions, response.chatSessions.sessions);
	});
}

export async function deleteChatSession(sessionId: string): Promise<{ sessionId: string }> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<DeleteSessionPayloadResponse>(DELETE_CHAT_SESSION, {
			input: { sessionId }
		});
		return extractResultOrThrow(response.deleteChatSession, response.deleteChatSession.session);
	});
}

export async function openChatSession(clientOpenId: string): Promise<string> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<OpenSessionPayloadResponse>(OPEN_CHAT_SESSION, {
			input: { clientOpenId }
		});
		return extractResultOrThrow(response.openChatSession, response.openChatSession.session).id;
	});
}

const CSV_COLUMN_MAPPING_SUGGESTION_TIMEOUT_MS = 3000;

const NO_CSV_COLUMN_MAPPING_SUGGESTION: CsvColumnMappingSuggestionResult = { fields: [], suggestionId: null };

export async function suggestCsvColumnMapping(
	input: SuggestCsvColumnMappingInput
): Promise<CsvColumnMappingSuggestionResult> {
	if (!Config.aiOrchestratorUrl) return NO_CSV_COLUMN_MAPPING_SUGGESTION;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), CSV_COLUMN_MAPPING_SUGGESTION_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return NO_CSV_COLUMN_MAPPING_SUGGESTION;
		const response = await createClient(token).request<SuggestCsvColumnMappingPayloadResponse>({
			document: SUGGEST_CSV_COLUMN_MAPPING,
			signal: controller.signal,
			variables: { input }
		});
		const { fields, suggestionId } = extractResultOrThrow(
			response.suggestCsvColumnMapping,
			response.suggestCsvColumnMapping
		);
		return { fields, suggestionId };
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("Jev column mapping suggestion failed", error);
		return NO_CSV_COLUMN_MAPPING_SUGGESTION;
	} finally {
		clearTimeout(timeout);
	}
}

export async function recordCsvColumnMappingOutcome(
	suggestionId: string,
	accepted: AcceptedCsvColumnMapping[]
): Promise<void> {
	try {
		const token = await getAccessToken();
		if (!token) return;
		const response = await createClient(token).request<RecordCsvColumnMappingOutcomePayloadResponse>(
			RECORD_CSV_COLUMN_MAPPING_OUTCOME,
			{ input: { accepted, suggestionId } }
		);
		extractResultOrThrow(response.recordCsvColumnMappingOutcome, response.recordCsvColumnMappingOutcome.decisionId);
	} catch (error) {
		logger.error("Jev column mapping outcome not recorded", error);
	}
}

const MISSING_DATA_RECOMMENDATION_TIMEOUT_MS = 3000;

export async function recommendMissingDataFix(findingKey: string): Promise<MissingDataRecommendation | null> {
	if (!Config.aiOrchestratorUrl) return null;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), MISSING_DATA_RECOMMENDATION_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return null;
		const response = await createClient(token).request<RecommendMissingDataFixPayloadResponse>({
			document: RECOMMEND_MISSING_DATA_FIX,
			signal: controller.signal,
			variables: { findingKey }
		});
		return extractResultOrThrow(response.recommendMissingDataFix, response.recommendMissingDataFix).recommendation;
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("Missing data recommendation failed", error);
		return null;
	} finally {
		clearTimeout(timeout);
	}
}

export async function recordMissingDataFixOutcome(
	suggestionId: string,
	outcome: MissingDataFixOutcome,
	acceptedId: null | string
): Promise<void> {
	const token = await getAccessToken();
	const response = await createClient(token).request<RecordMissingDataFixOutcomePayloadResponse>(
		RECORD_MISSING_DATA_FIX_OUTCOME,
		{ input: { acceptedId, outcome, suggestionId } }
	);
	extractResultOrThrow(response.recordMissingDataFixOutcome, response.recordMissingDataFixOutcome.decisionId);
}

const PAGE_MATCHES_TIMEOUT_MS = 3000;

export async function suggestPageMatches(searchText: string, pages: PageCandidate[]): Promise<PageSearchMatch[]> {
	if (!Config.aiOrchestratorUrl) return [];
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), PAGE_MATCHES_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return [];
		const response = await createClient(token).request<SuggestPageMatchesPayloadResponse>({
			document: SUGGEST_PAGE_MATCHES,
			signal: controller.signal,
			variables: { pages, searchText }
		});
		return extractResultOrThrow(response.suggestPageMatches, response.suggestPageMatches).pages;
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("Jev page search suggestions failed", error);
		return [];
	} finally {
		clearTimeout(timeout);
	}
}

const SUGGEST_FROM_LIST_TIMEOUT_MS = 3000;

const NO_LIST_SUGGESTION: SuggestFromListResult = { matches: [], suggestionId: null };

const withoutPayloadErrors = (result: SuggestFromListResult & { errors: { message: string }[] | null }) =>
	result.errors && result.errors.length > 0
		? NO_LIST_SUGGESTION
		: { matches: result.matches ?? [], suggestionId: result.suggestionId ?? null };

export async function suggestFromList(question: SuggestFromListQuestion): Promise<SuggestFromListResult> {
	if (!Config.aiOrchestratorUrl) return NO_LIST_SUGGESTION;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), SUGGEST_FROM_LIST_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return NO_LIST_SUGGESTION;
		const response = await createClient(token).request<SuggestFromListPayloadResponse>({
			document: SUGGEST_FROM_LIST,
			signal: controller.signal,
			variables: { input: question }
		});
		const { matches, suggestionId } = extractResultOrThrow(response.suggestFromList, response.suggestFromList);
		return { matches: matches ?? [], suggestionId: suggestionId ?? null };
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("fernie list suggestion failed", error);
		return NO_LIST_SUGGESTION;
	} finally {
		clearTimeout(timeout);
	}
}

export async function suggestFromListBatch(questions: SuggestFromListQuestion[]): Promise<SuggestFromListResult[]> {
	const nothing = questions.map(() => NO_LIST_SUGGESTION);
	if (!Config.aiOrchestratorUrl || questions.length === 0) return nothing;
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), SUGGEST_FROM_LIST_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return nothing;
		const response = await createClient(token).request<SuggestFromListBatchPayloadResponse>({
			document: SUGGEST_FROM_LIST_BATCH,
			signal: controller.signal,
			variables: { input: { questions } }
		});
		const { results } = extractResultOrThrow(response.suggestFromListBatch, response.suggestFromListBatch);
		return questions.map((_, index) => (results[index] ? withoutPayloadErrors(results[index]) : NO_LIST_SUGGESTION));
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("fernie batch list suggestion failed", error);
		return nothing;
	} finally {
		clearTimeout(timeout);
	}
}

export async function recordSuggestionOutcome(input: RecordSuggestionOutcomeInput): Promise<void> {
	if (!Config.aiOrchestratorUrl) return;
	try {
		const token = await getAccessToken();
		if (!token) return;
		const response = await createClient(token).request<RecordSuggestionOutcomePayloadResponse>(
			RECORD_SUGGESTION_OUTCOME,
			{ input }
		);
		extractResultOrThrow(response.recordSuggestionOutcome, response.recordSuggestionOutcome);
	} catch (error) {
		logger.error("fernie suggestion outcome not recorded", error);
	}
}

const ITEM_SEARCH_MATCHES_TIMEOUT_MS = 3000;

export async function suggestItemSearchMatches(searchText: string): Promise<ItemSearchMatch[]> {
	if (!Config.aiOrchestratorUrl) return [];
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), ITEM_SEARCH_MATCHES_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return [];
		const response = await createClient(token).request<SuggestItemSearchMatchesPayloadResponse>({
			document: SUGGEST_ITEM_SEARCH_MATCHES,
			signal: controller.signal,
			variables: { searchText }
		});
		return extractResultOrThrow(response.suggestItemSearchMatches, response.suggestItemSearchMatches).items;
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("Jev item search suggestions failed", error);
		return [];
	} finally {
		clearTimeout(timeout);
	}
}

const REPORT_DRAFTS_OFF: ReportDraftAvailability = {
	enabled: false,
	maxFileBytes: 0,
	maxPages: 0,
	remainingThisMonth: 0
};

const reportDraftFailure = (message: string): ReportDraftPayload => ({
	errors: [{ code: "MODEL_FAILED", message }],
	gaps: null,
	matches: null,
	outlineJson: null,
	runId: null
});

export async function fetchReportDraftAvailability(): Promise<ReportDraftAvailability> {
	if (!Config.aiOrchestratorUrl) return REPORT_DRAFTS_OFF;
	try {
		return await executeWithAuthRetry("query", async (token) => {
			const response = await createClient(token).request<ReportDraftAvailabilityResponse>(REPORT_DRAFT_AVAILABILITY);
			return response.reportDraftAvailability;
		});
	} catch (error) {
		logger.error("Report draft availability failed", error);
		return REPORT_DRAFTS_OFF;
	}
}

export async function designReportDraft(input: DesignReportDraftInput): Promise<ReportDraftPayload> {
	return executeWithAuthRetry("mutation", async (token) => {
		const response = await createClient(token).request<DesignReportDraftResponse>(DESIGN_REPORT_DRAFT, { input });
		return response.designReportDraft;
	});
}

export async function checkReportDraft(input: CheckReportDraftInput): Promise<ReportDraftPayload> {
	try {
		return await executeWithAuthRetry("mutation", async (token) => {
			const response = await createClient(token).request<CheckReportDraftResponse>(CHECK_REPORT_DRAFT, { input });
			return response.checkReportDraft;
		});
	} catch (error) {
		logger.error("Report draft check failed", error);
		return reportDraftFailure(hasMessage(error) ? error.message : "");
	}
}

const CUSTOMER_SEARCH_MATCHES_TIMEOUT_MS = 3000;

export async function suggestCustomerSearchMatches(searchText: string): Promise<CustomerSearchMatch[]> {
	if (!Config.aiOrchestratorUrl) return [];
	const controller = new AbortController();
	const timeout = setTimeout(() => controller.abort(), CUSTOMER_SEARCH_MATCHES_TIMEOUT_MS);
	try {
		const token = await getAccessToken();
		if (!token) return [];
		const response = await createClient(token).request<SuggestCustomerSearchMatchesPayloadResponse>({
			document: SUGGEST_CUSTOMER_SEARCH_MATCHES,
			signal: controller.signal,
			variables: { searchText }
		});
		return extractResultOrThrow(response.suggestCustomerSearchMatches, response.suggestCustomerSearchMatches).customers;
	} catch (error) {
		if (!isAbortError(error, controller.signal)) logger.error("Jev customer search suggestions failed", error);
		return [];
	} finally {
		clearTimeout(timeout);
	}
}

export type StreamStepPhase = "completed" | "started";

export type StreamStepPhaseStatus = "error" | "success";

export interface StreamStepEvent {
	label: string;
	phase: StreamStepPhase;
	status?: StreamStepPhaseStatus;
	toolName: string;
}

export interface StreamCallbacks {
	onComplete: (result: ChatResult) => void;
	onDelta: (text: string) => void;
	onError: (message: string) => void;
	onSession: (sessionId: string) => void;
	onStep: (step: StreamStepEvent) => void;
}

export class StreamConnectionError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "StreamConnectionError";
	}
}

export function resolveStreamEndpoint(graphqlUrl: string): string {
	const base = graphqlUrl.replace(/\/graphql\/?$/i, "").replace(/\/$/, "");
	return `${base}/chat/stream`;
}

function normalizeChatResult(raw: ChatResult): ChatResult {
	return {
		...raw,
		actions: raw.actions ?? [],
		artifacts: raw.artifacts ?? [],
		intakeCaptures: raw.intakeCaptures ?? [],
		proposals: raw.proposals ?? [],
		toolCalls: raw.toolCalls ?? [],
		usage: raw.usage ?? { inputTokens: 0, outputTokens: 0 }
	};
}

function parseEventData<T>(data: string): T | null {
	try {
		return JSON.parse(data) as T;
	} catch {
		return null;
	}
}

function dispatchStreamEvent(raw: RawSseEvent, callbacks: StreamCallbacks): boolean {
	switch (raw.event) {
		case "session": {
			const data = parseEventData<{ sessionId: string }>(raw.data);
			if (data?.sessionId) callbacks.onSession(data.sessionId);
			return false;
		}
		case "delta": {
			const data = parseEventData<{ text: string }>(raw.data);
			if (data && typeof data.text === "string") callbacks.onDelta(data.text);
			return false;
		}
		case "step": {
			const data = parseEventData<StreamStepEvent>(raw.data);
			if (data?.toolName) callbacks.onStep(data);
			return false;
		}
		case "complete": {
			const data = parseEventData<ChatResult>(raw.data);
			if (data) callbacks.onComplete(normalizeChatResult(data));
			else callbacks.onError("Fernie returned a malformed completion.");
			return true;
		}
		case "error": {
			const data = parseEventData<{ message: string }>(raw.data);
			callbacks.onError(data?.message || "Fernie ran into a problem.");
			return true;
		}
		default:
			return false;
	}
}

function toStreamErrorMessage(error: unknown): string {
	if (error instanceof Error) return error.message;
	return "Unexpected streaming error";
}

function isAbortError(error: unknown, signal: AbortSignal): boolean {
	return signal.aborted || (error instanceof Error && error.name === "AbortError");
}

async function consumeEventStream(
	body: ReadableStream<Uint8Array>,
	callbacks: StreamCallbacks,
	signal: AbortSignal
): Promise<void> {
	const reader = body.getReader();
	const decoder = new TextDecoder();
	let buffer = "";
	let terminal = false;
	try {
		while (!terminal) {
			const { done, value } = await reader.read();
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			const drained = drainSseEvents(buffer);
			buffer = drained.rest;
			for (const event of drained.events) {
				if (dispatchStreamEvent(event, callbacks)) terminal = true;
			}
		}
	} catch (error) {
		if (isAbortError(error, signal)) return;
		callbacks.onError(toStreamErrorMessage(error));
		return;
	}
	if (terminal) return;
	const flushed = drainSseEvents(`${buffer}\n\n`);
	for (const event of flushed.events) {
		if (dispatchStreamEvent(event, callbacks)) terminal = true;
	}
	if (!terminal) callbacks.onError("Fernie's response ended unexpectedly.");
}

export async function streamChatMessage(
	turnInput: ChatTurnInput,
	sessionId: string | null,
	activeModule: ChatModule | undefined,
	callbacks: StreamCallbacks,
	signal: AbortSignal,
	screenContext?: ScreenContext
): Promise<void> {
	const token = await getAccessToken();
	const headers: Record<string, string> = {
		...buildOrchestratorHeaders(token, activeModule),
		Accept: "text/event-stream",
		"Content-Type": "application/json"
	};
	let response: Response;
	try {
		response = await fetch(resolveStreamEndpoint(buildEndpoint()), {
			body: JSON.stringify(chatTurn.toRequestInput(turnInput, sessionId, screenContext)),
			headers,
			method: "POST",
			signal
		});
	} catch (error) {
		if (isAbortError(error, signal)) return;
		throw new StreamConnectionError(toStreamErrorMessage(error));
	}
	if (!response.ok || !response.body) {
		throw new StreamConnectionError(`Fernie streaming request failed with status ${response.status}`);
	}
	await consumeEventStream(response.body, callbacks, signal);
}
